/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HeroModule, MODULE_LAYOUTS, isInvertedColor } from '@coursera-web/cds-labs';

import { Button, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

type Props = {
  content?: MarketingContentFieldsFragment | null;
  secondaryButtonLabel?: string | null;
  secondaryButtonLink?: string | null;
  backgroundColor?: string | null;
};

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    && {
      text-decoration: none;
    }

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  hidden: css`
    display: none;
  `,
};

function MerchandisingHeroModuleRenderer({
  content,
  secondaryButtonLabel,
  secondaryButtonLink,
  backgroundColor,
}: Props) {
  const track = useTracker();

  if (!content) {
    return null;
  }

  const { description, callToActionLabel, callToActionLink, visual, title } = content || {};

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_button', {
      button: { name: buttonName, linkName, linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const descriptionToShow = (descriptionCopy?: string | null) => (
    <p css={styles.description}>{descriptionCopy ?? ''}</p>
  );

  const isDarkBackground = isInvertedColor(backgroundColor ?? '');

  const getHeroModuleProps = (): HeroModuleProps => {
    let imageHeight = visual?.height;
    let imageWidth = visual?.width;

    if (imageHeight && imageWidth) {
      imageWidth = 720;
      imageHeight = (imageHeight / imageWidth) * 720;
    }

    return {
      headline: title,
      headlineComponent: 'h2',
      description: descriptionToShow(description),
      image: {
        url: visual?.url,
        description: visual?.description,
        width: visual?.width,
        height: visual?.height,
      },
      layout: MODULE_LAYOUTS.text6image6,
      showImagesAboveContentOnMobile: false,
      backgroundColor,
      preloadImage: true,
      customPrimaryCta:
        callToActionLabel && callToActionLink ? (
          <Button
            variant={isDarkBackground ? 'secondary' : 'primary'}
            component="a"
            href={callToActionLink}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_primary_cta', callToActionLabel, callToActionLink)}
          >
            {callToActionLabel}
          </Button>
        ) : undefined,
      customSecondaryCta:
        secondaryButtonLabel && secondaryButtonLink ? (
          <Button
            variant={isDarkBackground ? 'primary' : 'secondary'}
            component="a"
            href={secondaryButtonLink}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_secondary_cta', secondaryButtonLabel, secondaryButtonLink)}
          >
            {secondaryButtonLabel}
          </Button>
        ) : undefined,
    };
  };

  return (
    <div css={styles.container}>
      <SeoPageHeader />
      <HeroModule {...getHeroModuleProps()} />
    </div>
  );
}

export default MerchandisingHeroModuleRenderer;
